import { each, pipe, tap } from '@fxts/core';
import { ScrollManager, ScrollConfig } from './core';

export class InfiniteScrollManager<D, C> extends ScrollManager<D, C> {
  private intersectionObserver?: IntersectionObserver;
  protected sentinel!: HTMLElement;

  constructor(config: ScrollConfig<D, C>) {
    super(config);

    this.initialize();
  }

  protected initialize() {
    this.sentinel = this.createSentinel();
    this.container.append(this.sentinel);

    this.intersectionObserver = new IntersectionObserver(this.callback.bind(this), {
      root: this.container,
      rootMargin: this.calculateRootMargin(),
      threshold: this.calculateThreshold(),
    });
  }

  protected render(data: D[]) {
    if (!this.config.onRender) return;

    const elements = this.config.onRender(data);

    pipe(
      document.createDocumentFragment(),
      tap((fragment) => fragment.append(...elements)),
      (fragment) => this.container.insertBefore(fragment, this.sentinel),
    );
  }

  private createSentinel(): HTMLElement {
    const $sentinel = document.createElement('div');
    $sentinel.style.height = '1px';
    $sentinel.style.visibility = 'hidden';
    return $sentinel;
  }

  private calculateRootMargin() {
    const rootMargin = this.config.threshold || 200;
    return `0px 0px ${rootMargin}px 0px`;
  }

  private callback(entries: IntersectionObserverEntry[]) {
    pipe(
      entries,
      each(async (entry) => {
        if (entry.isIntersecting && this.hasMore) {
          await this.load();
        } else {
          this.config.onEnd?.();
        }
      }),
    );
  }

  public on(): this {
    if (this.isInitialized) this.reset();

    this.intersectionObserver?.observe(this.sentinel);

    requestAnimationFrame(() => {
      this.container.scrollTop = this.scroll;
    });

    this.createScrollEvent();
    return this;
  }

  public off(): this {
    this.intersectionObserver?.unobserve(this.sentinel);
    return this;
  }

  public reset(): this {
    this.container = this.createContainer();
    this.initialize();
    return this;
  }

  public destroy(): this {
    this.items.clear();
    this.intersectionObserver?.disconnect();
    return this;
  }
}
